import { fetchAuthSession } from "aws-amplify/auth";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com";

export type InvoiceRole = "OWNER" | "INTEGRATOR";
export type InvoiceStatus = "OPEN" | "FINALIZED";
export type PaymentCollectionStatus =
  | "NOT_CONFIGURED"
  | "READY"
  | "PROCESSING"
  | "PAID"
  | "FAILED"
  | "ACTION_REQUIRED";

export type InvoiceUsage = {
  storageTbMonth: number;
  liveTransferGb: number;
  archivePlaybackGb: number;
  cloudAnalyticsImages: number;
  aiInputTokens: number;
  aiOutputTokens: number;
};

export type InvoiceChargeBreakdown = {
  basePlan: number;
  storage: number;
  liveTransfer: number;
  archivePlayback: number;
  cloudAnalytics: number;
  aiInput: number;
  aiOutput: number;
  aiMonitoring: number;
  total: number;
};

export type CameraInvoiceLine = {
  invoiceId: string | null;
  status: InvoiceStatus;
  cameraId: string;
  cameraName: string;
  partnerId: string;
  partnerName: string;
  customerId: string;
  customerName: string;
  customerEmail?: string | null;
  planName: string;
  billingModel: "FIXED" | "PAYG";
  analyticsTier: string;
  currency: string;
  cycle: {
    start: string;
    end: string;
    daysRemaining: number;
  };
  usage: InvoiceUsage;
  retailRates: {
    aiInputPerMillionTokens: number;
    aiOutputPerMillionTokens: number;
  };
  retailCharges: InvoiceChargeBreakdown;
  wholesaleRates?: {
    aiInputPerMillionTokens: number;
    aiOutputPerMillionTokens: number;
  };
  wholesaleCharges?: InvoiceChargeBreakdown;
  margin?: number;
  paymentStatus?: PaymentCollectionStatus;
  retailPaymentStatus?: PaymentCollectionStatus;
  wholesalePaymentStatus?: PaymentCollectionStatus;
};

export type InvoiceSummary = {
  cameraCount: number;
  customerCount: number;
  integratorCount: number;
  retailTotal: number;
  wholesaleTotal?: number;
  margin?: number;
  aiMonitoringTotal: number;
  wholesaleAiMonitoringTotal: number;
};

export type InvoicesResponse = {
  role: InvoiceRole;
  currency: string;
  generatedAt: string;
  summary: InvoiceSummary;
  current: CameraInvoiceLine[];
  history: CameraInvoiceLine[];
  historyLimited?: boolean;
};

async function authToken() {
  const session = await fetchAuthSession();
  const value = session.tokens?.idToken?.toString();
  if (!value) {
    throw new Error("Your session has expired. Sign out and sign in again.");
  }
  return value;
}

export async function getInvoices(): Promise<InvoicesResponse> {
  const token = await authToken();
  const response = await fetch(`${API_BASE}/billing/invoices`, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(
      payload?.message || text || `Unable to load invoices (${response.status})`,
    );
  }
  return payload as InvoicesResponse;
}
